import { Queue } from 'utility/queue';
import { StreamSelectorInterface, StreamSelectorResult } from './StreamSelectorBase';
import { StreamSelectorByBitrate } from './StreamSelectorByBitrate';
import { StreamSelectorByFramerate } from './StreamSelectorByFramerate';
import { StreamSelectorByQP } from './StreamSelectorByQP';
import { StreamSelectorByResolution } from './StreamSelectorByResolution';
import { StreamSelectorByRisingBitrate } from './StreamSelectorByRisingBitrate';
import { Stabilizer, StreamSelectorStabilizer } from './StreamSelectorStabilizer';
import { isRTCInboundRtpVideoStreamStats, RTCInboundRtpVideoStreamStats } from './types';

export class StreamSelector {
    private statsTable: Queue<RTCInboundRtpVideoStreamStats>[] = [];
    private selectors: StreamSelectorInterface[];

    private currentStream = 0;

    constructor(numberOfStreams: number, private readonly debugTag?: string) {
        if (this.debugTag) {
            console.log('stream selector v2');
        }

        const stabilizer = new Stabilizer();

        this.selectors = [
            // avoiding unstable stream change
            new StreamSelectorStabilizer(stabilizer),
            // check by communication quality
            //  - find stream which bitrate is rapidly rising
            new StreamSelectorByRisingBitrate(numberOfStreams)
                // this selection should be stable for a while
                .filtered(result => {
                    if (typeof result === 'number') {
                        stabilizer.reset();
                    }
                    return result;
                }),
            //  - by bitrate
            new StreamSelectorByBitrate(numberOfStreams),
            // check by video quality
            //  - by resolution
            new StreamSelectorByResolution(),
            //  - by framerate
            new StreamSelectorByFramerate(),
            //  - by QP
            new StreamSelectorByQP()
        ];
    }

    get index() { return this.currentStream; }

    push(...streams: (RTCStatsReport | undefined)[]) {
        streams.forEach((stream, index) => {
            const stats = this.statsTable[index] = this.statsTable.at(index) ?? new Queue(100);
            if (stream) {
                stream.forEach((report: RTCStats) => {
                    if (isRTCInboundRtpVideoStreamStats(report)) {
                        stats.push(report);
                    }
                });
            } else {
                stats.clear();
            }
        });
    }

    getTargetStreamIndex(): number {
        try {
            const input = this.statsTable.map(q => [...q.array]);
            this.selectors.forEach(selector => {
                selector.batchUpdate(this.statsTable);
            });

            // get the first determined result
            const context: Record<string, unknown>[] = [];
            let result: StreamSelectorResult | undefined;
            const selector = this.selectors.find(selector => {
                result = selector.get();
                if (this.debugTag) {
                    context.push({ label: selector.label(), context: selector.description() });
                }
                switch (result) {
                    case 'matched':
                        return false;
                    case 'deferred':
                        return true;
                    default:
                        this.currentStream = result;
                        return true;
                }
            });
            if (this.debugTag) {
                const tag = this.debugTag;
                if (selector) {
                    console.log(JSON.stringify({ tag, selectedBy: selector.label(), result, context, input }));
                } else {
                    console.log(JSON.stringify({ tag, notSelected: result, context, input }));
                }
            }

            return this.currentStream;
        } catch (e) {
            console.error(e, this.debugTag);
            throw e;
        }
    }
}
